// Backlog #16: acknowledgement-required messages.

export type AckAudience = 'all' | 'selected';
/** outstanding: needs to acknowledge · acknowledged: done for the current wording · changed: acknowledged an earlier
 *  wording (only when re-acknowledgement is not required; otherwise that counts as outstanding) · left: no longer in the conversation. */
export type AckStatus = 'outstanding' | 'acknowledged' | 'changed' | 'left';
export type AckSource = 'snapshot' | 'joined' | 'selected' | 'team';

export interface AckRequestInput {
  audience: AckAudience;
  /** audience 'selected': people and/or teams / groups (expanded to their members who are in the conversation) */
  user_ids?: string[];
  team_ids?: string[];
  /** audience 'all': people who join the conversation later are added (default true) */
  include_new_members?: boolean;
  due_at?: string | null;
  /** editing the message makes everyone acknowledge again (default false: they are flagged "changed since") */
  require_reack?: boolean;
}

export interface AckRecipientView {
  user_id: string;
  display_name: string;
  source: AckSource;
  status: AckStatus;
  acknowledged_at: string | null;
  /** content version they last acknowledged */
  acknowledged_version: number | null;
  added_at: string;
}

export interface AckView {
  message_id: string;
  conversation_id: string;
  audience: AckAudience;
  include_new_members: boolean;
  team_names: string[];
  due_at: string | null;
  require_reack: boolean;
  content_version: number;
  content_changed_at: string | null;
  created_by: string;
  created_by_name: string;
  created_at: string;
  cancelled_at: string | null;
  counts: { total: number; acknowledged: number; outstanding: number; changed: number; left: number };
  /** my own status, null when I'm not a recipient */
  my_status: AckStatus | null;
  my_acknowledged_at: string | null;
  can_manage: boolean;
  /** only for people who can manage */
  recipients?: AckRecipientView[];
  last_reminded_at?: string | null;
  /** t239u: the message preview, where it was posted and its link (the phone status page). */
  preview?: string;
  conversation_name?: string;
  link?: string;
}

export interface AckListItem {
  message_id: string;
  conversation_id: string;
  conversation_name: string;
  parent_id: string | null;
  preview: string;
  /** t163: opens the person drawer. */
  author_id?: string | null;
  author_name: string;
  posted_at: string;
  due_at: string | null;
  status: AckStatus;
  counts?: AckView['counts'];
  link: string;
  /** Pending box only: I've already scrolled past it in the conversation (top-level messages; thread replies count as unread). */
  read?: boolean;
  /** Mine box (t129): when I last acknowledged it, if I have. */
  acknowledged_at?: string | null;
}

export interface AckBulkResult { acknowledged: string[]; skipped: { message_id: string; reason: 'unread' | 'not_pending' | 'error' }[] }

export type AckDueFilter = 'all' | 'overdue' | 'due_soon' | 'no_due';
/** Filters for the acknowledgements page (review Low). due_soon = due within 24 hours. */
export function filterAcks<T extends Pick<AckListItem, 'due_at' | 'read'>>(items: T[], f: { due: AckDueFilter; unreadOnly?: boolean }, now = Date.now()): T[] {
  return items.filter(i => {
    if (f.unreadOnly && i.read) return false;
    const due = i.due_at ? Date.parse(i.due_at) : null;
    if (f.due === 'overdue') return due !== null && due < now;
    if (f.due === 'due_soon') return due !== null && due >= now && due - now <= 24 * 3600_000;
    if (f.due === 'no_due') return due === null;
    return true;
  });
}

/** Per-recipient status from the version they last acknowledged. */
export function ackStatus(ackedVersion: number | null, currentVersion: number, requireReack: boolean, isMember = true): AckStatus {
  if (!isMember) return 'left';
  if (ackedVersion === null) return 'outstanding';
  if (ackedVersion >= currentVersion) return 'acknowledged';
  return requireReack ? 'outstanding' : 'changed';
}

/** Counts exclude people who left from the total ("18/25 acknowledged"). "Changed" counts as acknowledged. */
export function ackCounts(statuses: AckStatus[]): AckView['counts'] {
  const c = { total: 0, acknowledged: 0, outstanding: 0, changed: 0, left: 0 };
  for (const s of statuses) {
    if (s === 'left') { c.left++; continue; }
    c.total++;
    if (s === 'outstanding') c.outstanding++; else { c.acknowledged++; if (s === 'changed') c.changed++; }
  }
  return c;
}

export const ACK_STATUS_LABEL: Record<AckStatus, string> = {
  outstanding: 'Not yet', acknowledged: 'Acknowledged', changed: 'Acknowledged (before last edit)', left: 'Left the conversation',
};

export function validateAckInput(b: AckRequestInput, now = new Date()): Record<string, string> {
  const e: Record<string, string> = {};
  if (b.audience !== 'all' && b.audience !== 'selected') e.audience = 'Choose everyone or selected people';
  if (b.audience === 'selected' && !(b.user_ids?.length || b.team_ids?.length)) e.user_ids = 'Pick at least one person or team';
  if ((b.user_ids?.length ?? 0) > 500 || (b.team_ids?.length ?? 0) > 50) e.user_ids = 'Too many recipients';
  if (b.due_at) {
    const t = Date.parse(b.due_at);
    if (Number.isNaN(t)) e.due_at = 'Invalid due date';
    else if (t <= now.getTime()) e.due_at = 'The due date must be in the future';
    else if (t > now.getTime() + 366 * 86_400_000) e.due_at = 'The due date must be within a year';
  }
  return e;
}

/** t129: "My acknowledgements" lists everything I was asked to acknowledge; still-to-do ones first. */
export type AckBox = 'mine' | 'pending' | 'requested';
export const isAckPending = (i: Pick<AckListItem, 'status'>) => i.status === 'outstanding';
